import { apiPost } from './_common';

export type SpeakerCredentials = {
  ip: string;
  port: string;
  id: string;
  pwd: string;
};

const RELAY_BASE = process.env.REACT_APP_BASE_URL;

export const LOOPBACK_KEYWORDS = ['stereo mix', 'what u hear', 'wave out mix', 'loopback', 'wasapi', '스테레오 믹스'];

export function isLoopbackDevice(label: string): boolean {
  return LOOPBACK_KEYWORDS.some((kw) => label.toLowerCase().includes(kw));
}

// Persisted sys stream — survives React component unmount/remount
let persistedSysStream: MediaStream | null = null;

export function getPersistedSysStream(): MediaStream | null {
  return persistedSysStream;
}

export function setPersistedSysStream(stream: MediaStream | null): void {
  persistedSysStream = stream;
}

/**
 * Requests mic permission, then selects the real microphone.
 * If the browser defaults to a loopback device (Stereo Mix), explicitly picks
 * the first non-loopback input so the human voice is captured correctly.
 */
export async function getMicStream(): Promise<MediaStream> {
  const permStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

  const allDevices = await navigator.mediaDevices.enumerateDevices();
  const audioInputs = allDevices.filter((d) => d.kind === 'audioinput');
  const realMicDevices = audioInputs.filter((d) => !isLoopbackDevice(d.label) && d.deviceId !== 'default' && d.deviceId !== 'communications');

  const defaultTrackLabel = permStream.getAudioTracks()[0]?.label ?? '';
  if (isLoopbackDevice(defaultTrackLabel) && realMicDevices.length > 0) {
    permStream.getTracks().forEach((track) => track.stop());
    return navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: realMicDevices[0].deviceId } },
      video: false,
    });
  }
  return permStream;
}

/**
 * Returns the loopback (Stereo Mix) audio stream, reusing persistedSysStream
 * if it is still alive. Returns null if no loopback device is found.
 * Must be called after getMicStream() so device labels are visible.
 *
 * getDisplayMedia 로 폴백하지 않는다. 브라우저가 호출마다 공유 대상 선택 창을 띄우고
 * 허용을 기억하지 않아 방송 시작 때마다 창이 떴다. 모달의 채임·음원·TTS 는
 * 믹스 버스에 직접 연결되므로 시스템 오디오 없이도 송출된다.
 */
export async function getSysStream(): Promise<MediaStream | null> {
  const isSysAlive = persistedSysStream?.getAudioTracks().some((track) => track.readyState === 'live') ?? false;
  if (isSysAlive) return persistedSysStream;

  persistedSysStream = null;

  const allDevices = await navigator.mediaDevices.enumerateDevices();
  const loopbackDevices = allDevices.filter((d) => d.kind === 'audioinput' && isLoopbackDevice(d.label));
  if (loopbackDevices.length === 0) return null;

  let sysStream: MediaStream | null = null;

  try {
    sysStream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: loopbackDevices[0].deviceId } },
      video: false,
    });
  } catch {
    // user denied or device unavailable — mic-only broadcast
  }

  if (sysStream) {
    persistedSysStream = sysStream;
    sysStream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        persistedSysStream = null;
      });
    });
  }

  return sysStream;
}

/** Opens a relay session on the backend. Throws if the backend returns non-200. */
export async function createRelaySession(speakers: SpeakerCredentials[], channel = 1): Promise<string> {
  const res = await apiPost({
    path: '/bs/isapi/talk/session',
    req: { speakers, channel },
    contentType: 'application/json',
  });
  if (res?.status !== 200) throw new Error('릴레이 세션 생성에 실패했습니다.');
  return res.data.data as string;
}

/** Sends a PCM Int16 16 kHz audio chunk to the relay backend for server-side G.711 conversion. */
export async function sendRelayChunk(sessionId: string, chunk: ArrayBuffer, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`${RELAY_BASE}/bs/isapi/talk/data?session=${sessionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'audio/pcm;rate=16000', Authorization: 'Bearer relay' },
    body: chunk,
    signal,
  });
  // fetch 는 4xx/5xx 에서도 resolve 하므로 상태를 명시적으로 확인해야
  // 릴레이가 청크를 거부하는 상황(세션 만료 등)을 호출부가 감지할 수 있다.
  if (!res.ok) throw new Error(`relay chunk rejected: HTTP ${res.status}`);
}

/** Closes a relay session on the backend (fire-and-forget). */
export function closeRelaySession(sessionId: string): void {
  fetch(`${RELAY_BASE}/bs/isapi/talk/close?session=${sessionId}`, { method: 'PUT' }).catch(() => {});
}

// ─── PCM helpers ─────────────────────────────────────────────────────────────

/** Converts a Float32 audio buffer (−1…1) to Int16 LE for transmission. */
export function float32ToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    out[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
  }
  return out;
}

// ─── FIR downsampling ────────────────────────────────────────────────────────

const FIR_TAPS = 127; // odd → integer group delay of (TAPS−1)/2 = 63 input samples

/**
 * 릴레이의 최종 출력 레이트. 백엔드가 16 kHz PCM 을 G.711 µ-law 8 kHz 로 변환하므로
 * 안티에일리어싱 기준은 중간 전송 레이트(16 kHz)가 아니라 이 값이어야 한다.
 */
export const FINAL_OUTPUT_RATE = 8000;

/**
 * Builds a 127-tap Blackman-windowed sinc low-pass FIR filter.
 * Cutoff is set at 87.5 % of the FINAL output Nyquist (3 500 Hz for 8 kHz output)
 * to leave a clean transition band before decimation.
 *
 * 두 번째 인자는 전송 레이트가 아니라 **최종 출력 레이트**다.
 * 백엔드의 16 k → 8 k 변환은 인접 2 샘플 평균(2-tap 박스 필터)이라
 * 4 kHz 에서 감쇠가 −3 dB 에 불과하다. 여기서 최종 나이퀴스트(4 kHz) 기준으로
 * 미리 잘라두지 않으면 4~7 kHz 성분이 4 kHz 아래로 접혀(에일리어싱)
 * 원음에 없던 잡음이 섞이고 지직거림으로 들린다.
 */
export function buildFirCoefficients(fromRate: number, finalOutputRate: number): Float32Array {
  const cutoffHz = (finalOutputRate / 2) * 0.875; // 3 500 Hz for 8 kHz output
  const fc = cutoffHz / fromRate;
  const M = FIR_TAPS - 1;
  const h = new Float32Array(FIR_TAPS);

  for (let n = 0; n < FIR_TAPS; n += 1) {
    const i = n - M / 2;
    const w = 0.42 - 0.5 * Math.cos((2 * Math.PI * n) / M) + 0.08 * Math.cos((4 * Math.PI * n) / M);
    const s = i === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * i) / (Math.PI * i);
    h[n] = s * w;
  }

  let sum = 0;
  for (let n = 0; n < FIR_TAPS; n += 1) sum += h[n];
  for (let n = 0; n < FIR_TAPS; n += 1) h[n] /= sum;

  return h;
}

/** 리샘플러의 호출 간 위상 상태. 세션당 하나 만들어 재사용한다. */
export type ResamplePhase = { offset: number };

/** 위상 상태를 초기화한다. */
export function createResamplePhase(): ResamplePhase {
  return { offset: 0 };
}

/**
 * Decimate `input` from `fromRate` to `toRate` using pre-computed FIR
 * coefficients for anti-aliasing.  `delayLine` (length = coeffs.length − 1)
 * carries the tail of the previous buffer so there are no discontinuities
 * at buffer boundaries.  `coeffs`, `delayLine`, `phase` must all be created
 * once per broadcast session and reused on every onaudioprocess call.
 *
 * `phase` 가 필요한 이유: 버퍼 길이가 step 의 배수가 아니면(1024 ÷ 3 = 341.33)
 * 매 호출마다 나머지 샘플만큼 출력 위상이 어긋난다. 그 경계에서만 출력 간격이
 * step 이 아닌 step+1 이 되어, 콜백 주기(21.33ms ≒ 46.9Hz)의 주기적 왜곡이
 * 광대역 클릭으로 들린다. 다음 호출의 시작 오프셋을 이어받아 이를 없앤다.
 */
export function downsampleFloat32(input: Float32Array, fromRate: number, toRate: number, coeffs: Float32Array, delayLine: Float32Array, phase: ResamplePhase): Float32Array {
  const step = Math.round(fromRate / toRate);
  const startOffset = phase.offset;
  // startOffset 부터 step 간격으로 input 범위 안에 들어오는 출력 샘플 개수
  const outLen = input.length > startOffset ? Math.floor((input.length - startOffset - 1) / step) + 1 : 0;
  const out = new Float32Array(outLen);
  const taps = coeffs.length;

  // 이번 호출이 소비한 위치를 다음 호출의 시작 오프셋으로 이어준다.
  phase.offset = startOffset + outLen * step - input.length;

  for (let i = 0; i < outLen; i += 1) {
    const newest = startOffset + i * step;
    let acc = 0;
    for (let k = 0; k < taps; k += 1) {
      const srcIdx = newest - k;
      let sample: number;
      if (srcIdx >= 0) {
        sample = input[srcIdx];
      } else {
        // Sample lives in the previous buffer's tail
        // delayLine[0] = oldest, delayLine[taps-2] = most recent past
        const dlIdx = delayLine.length + srcIdx; // srcIdx is negative
        sample = dlIdx >= 0 ? delayLine[dlIdx] : 0;
      }
      acc += coeffs[k] * sample;
    }
    out[i] = acc;
  }

  // Slide the delay line: keep the last (taps−1) input samples for next call
  const keep = taps - 1;
  if (input.length >= keep) {
    for (let i = 0; i < keep; i += 1) {
      delayLine[i] = input[input.length - keep + i];
    }
  } else {
    const shift = keep - input.length;
    for (let i = 0; i < shift; i += 1) delayLine[i] = delayLine[i + input.length];
    for (let i = 0; i < input.length; i += 1) delayLine[shift + i] = input[i];
  }

  return out;
}
